/**
 * Recovery when a Team member can't find what Ask Noticeboard just pointed them
 * to: "I don't see it", "where is that?", "it's not there", "that didn't work"
 * and "what now?" all mean the same thing — stuck on the last answer, not a new
 * question — so they're read the same way wherever they land in the chat.
 */
import { getSidebarItem, sidebarConfig, type SidebarItemKey, type SidebarNavItem } from "../../config/sidebarConfig";
import type { AuthUser } from "../../types/authUser";
import { getSidebarItemAccess } from "../navigation/sidebarAccess";
import type { HowToAction } from "./catalogue";
import { aRole, listOf } from "./catalogue/wording";
import { COURSE_PATH } from "../help/course";

/** "I don't see it", "where is that?", "it's not there", "that didn't work", "what now?" */
const STUCK =
  /^(?:(?:ok(?:ay)?|no|hmm+)[,\s]+)*(?:i\s+)?(?:(?:don'?t|can'?t|couldn'?t)\s+(?:see|find)\s+(?:it|that(?:\s+(?:button|page|tab|link))?)|it'?s\s+not\s+there|it\s+isn'?t\s+there|where'?s\s+that|where\s+is\s+that|that\s+didn'?t\s+work|what\s+(?:do\s+i\s+do\s+)?now|still\s+(?:don'?t\s+see\s+it|can'?t\s+(?:see|find)\s+it))(?:[.!?]+\s*(?:where(?:'s| is) that|what(?: do i do)? now))?[\s.!?]*$/i;

/** Stuck on the last answer, not a new question: never worth matching against the catalogue. */
export const isStuckPhrase = (text: string): boolean => STUCK.test(text.trim());

/** The sidebar items this person can actually see, in the order they're shown, for describing where one sits. */
const visibleItems = (user: AuthUser | null | undefined): SidebarNavItem[] =>
  sidebarConfig.navItems.filter((item) => getSidebarItemAccess(item, user) !== "hidden");

/** Where a page sits in the sidebar, since there are no section headings to point to. */
function positionInSidebar(item: SidebarNavItem, user: AuthUser | null | undefined): string {
  const items = visibleItems(user);
  const index = items.findIndex((i) => i.key === item.key);
  const spot =
    index <= 0 ? "at the top of the sidebar on the left" : `in the sidebar on the left, just below ${items[index - 1].label}`;
  return `${item.label} is ${spot}. On a phone, tap the menu icon at the top left to open the sidebar; if yours is collapsed to icons, hover over one to see its name.`;
}

export type PageStuckReply =
  | { kind: "pointAgain"; text: string; link: { label: string; href: string } }
  | { kind: "needsRole"; text: string };

/**
 * After a page answer, when the person still can't find it: where it sits in the
 * sidebar and a button to it, or — when this role can't open that page at all —
 * who can, instead of a button that wouldn't work for them.
 */
export function pageStuckReply(sidebarItem: SidebarItemKey, user: AuthUser | null | undefined): PageStuckReply {
  const item = getSidebarItem(sidebarItem);
  if (getSidebarItemAccess(item, user) === "hidden") {
    return {
      kind: "needsRole",
      text: `${item.label} is only for ${listOf(item.teamRoles.map(aRole))}. Ask one to open it for you.`,
    };
  }
  return { kind: "pointAgain", text: positionInSidebar(item, user), link: { label: `Open ${item.label}`, href: item.path } };
}

/** A how-to answer's steps, split the way they're written ("Drive → Upload & Share → …"). */
export function stepsOf(howToText: string): string[] {
  const chain = howToText.split("→").map((step) => step.trim()).filter(Boolean);
  if (chain.length < 2) return chain;
  // "…then press Upload & Share. Nothing is uploaded until you press that.": the chain ends with its own sentence
  chain[chain.length - 1] = chain[chain.length - 1].replace(/\.\s+[A-Z].*$/, ".");
  // "add the file, choose the Circle, then press Upload & Share.": one step per thing to do, named as the screen does
  return chain
    .flatMap((step) =>
      /,\s*then\s|\s+then\s/i.test(step)
        ? step.split(/,\s*(?:then\s+)?|\s+then\s+/i).map((part) => part.replace(/[.\s]+$/, ""))
        : [step]
    )
    .map((step) => step.replace(/^(?:press|tap|click)\s+/i, "").trim())
    .filter(Boolean);
}

export const WHICH_STEP_REPLY = "Which step are you stuck on?";

export const STILL_STUCK_REPLY =
  "Let's get you unstuck another way: the Help Center has short videos for this, and Noticeboard Fundamentals walks through the basics step by step.";

const helpCenterPage = getSidebarItem("helpCenter");

/** Help Center and the Noticeboard Fundamentals course, offered once pointing back at the same answer hasn't helped. */
export const STILL_STUCK_ACTIONS: HowToAction[] = [
  { kind: "open", label: `Open ${helpCenterPage.label}`, to: { pathname: helpCenterPage.path, state: undefined } },
  { kind: "open", label: "Take Noticeboard Fundamentals", to: { pathname: COURSE_PATH, state: undefined } },
];
